/**
 * The terrible bargain (M4-05).
 *
 * Two verbs at the top of the medicine ladder, and they are the darkest things
 * in the game that are not violence. **Extraction** digs a fragment out of a
 * wound that has been quietly festering around it since the boar put it there;
 * **amputation** trades a limb for the life attached to it. Both are surgery in
 * a century with no anaesthetic, which is the whole design: they are loud, they
 * are slow, they hurt, and they can kill the patient on the table.
 *
 * Three commitments worth stating outright, because each was a choice:
 *
 * 1. **The gangrene is stopped by the cut, not by the roll.** A successful
 *    amputation removes the rotten part and with it every wound on it — the
 *    infection track does not get a save. What *is* rolled is whether it was
 *    already past the joint (`GANGRENE_STOPPED`), which is the honest failure
 *    and the one that produces the second, worse conversation.
 * 2. **The stump is a real wound.** It goes through `createWound` and
 *    `applyWound` like a spear thrust, with its own bleed, pain and infection
 *    modifier, so the man who survives the axe can still die of it that night.
 *    A "surgery result" that was not a wound would have been a second injury
 *    system, and the point of `wound.ts` is that there is only one.
 * 3. **Consent is state, not a client modal.** `Combatant.consent` is the
 *    patient's word, checked at the moment the axe falls. A conscious player
 *    must give it; an unconscious one cannot, and the verb proceeds anyway.
 *    That asymmetry is deliberate and is the ticket's: it makes trust a
 *    mechanic, and it makes carrying your unconscious friend to *somebody* a
 *    decision with a person's name in it.
 *
 * The arithmetic — how many dice a wit buys, what darkness costs — is
 * `medicine.ts`'s, exactly as it is for the rest of the ladder. What is here is
 * what the knife does.
 */
import { capabilityFault } from './affordance.js';
import { chebyshev } from './actor.js';
import { parseAmputate, parseConsent, parseExtract } from './actions.js';
import { deriveCapabilities } from './body.js';
import { fireAt } from './camp.js';
import { learnMedicine, nothingToTreat, patientPossessive, workingBlind } from './care.js';
import { isHelpless, kitOf } from './fighter.js';
import { infectionStageOf, isLimbZone } from './infection.js';
import { addItem, createInventory } from './item.js';
import { rollTreatment, treatmentDice } from './medicine.js';
import { TICKS_PER_SECOND } from './time.js';
import { feltPain } from './vitals.js';
import { applyWound, createWound, isLodged, LODGED_INFECTION_MOD, MAX_WOUND_LEVEL, } from './wound.js';
/* -------------------------------------------------------------------------- *
 * Timings and tools
 * -------------------------------------------------------------------------- */
/**
 * Twenty seconds of digging in somebody's thigh with a knife, and forty-five
 * for the axe.
 *
 * Both are far longer than anything else on the ladder, and the length is the
 * mechanic: a procedure is a commitment to a place. The surgeon cannot be
 * moved, cannot fight, and the log has been announcing what they are doing to
 * everyone in sight since they started. If the wolves come back, the choice of
 * whether to finish is a real one.
 */
export const EXTRACT_TICKS = TICKS_PER_SECOND * 20;
export const AMPUTATE_TICKS = TICKS_PER_SECOND * 45;
/** What a blade has to be good for to dig with, and to take a limb off with. */
export const FINE_USE = 'fine';
export const CLEAVE_USE = 'cleave';
/** The two verbs this module owns, for the channel loop's dispatch. */
const SURGICAL_VERBS = new Set(['extract', 'amputate']);
export function isSurgicalVerb(verb) {
    return SURGICAL_VERBS.has(verb);
}
/* -------------------------------------------------------------------------- *
 * Balance
 * -------------------------------------------------------------------------- */
/**
 * Dice for working on somebody who cannot flinch.
 *
 * The grimmest number in the file and the most defensible: the hardest part of
 * digging iron out of a man is the man. AoD's held-or-willing patient is the
 * baseline; senseless is a die better, and it is why a surgeon may quietly
 * prefer not to wake somebody first.
 */
export const SENSELESS_BONUS = 1;
/** What the fine blade in your hand is worth to the dig. */
export const BLADE_BONUS = 1;
/** The level of the fresh wound a failed dig leaves. Try again, and hurry. */
const FAILED_DIG_LEVEL = 1;
/** The level of an amputation stump: serious, always, however well it went. */
const STUMP_LEVEL = 3;
/**
 * How often taking the limb off actually stops the rot (the ticket's ~85%).
 *
 * Not 100%, because the honest answer to "was the gangrene above the cut" is
 * that nobody in this century can tell until afterwards. The 15% is the case
 * that makes the first amputation frightening rather than a procedure, and it
 * lands as a stump wound that is already infected — the second conversation,
 * with less leg to have it about.
 */
export const GANGRENE_STOPPED = 0.85;
/**
 * Extra pain an awake patient takes from the saw, before relief.
 *
 * Large, and meant to be: a conscious amputation without a drink or a willow
 * brew in them is very likely to put them out, which is the mercy the fiction
 * offers and the reason a surgeon dose their patient first. `feltPain` subtracts
 * relief, so the mead and the brew are read here through the same seam that
 * quiets every other pain in the game (M4-03).
 */
export const AWAKE_PAIN = 8;
/** How far a scream carries. Further than a cautery: this one goes on. */
export const SURGERY_EARSHOT = 24;
/** How long a patient's word is good for. A minute — an answer, not a contract. */
export const CONSENT_TICKS = TICKS_PER_SECOND * 60;
/* -------------------------------------------------------------------------- *
 * What there is to work on
 * -------------------------------------------------------------------------- */
/**
 * The wound with something still in it — the named part's, or the worst.
 *
 * "Worst" is the standing infection risk rather than the level, because that is
 * what extraction is *for*: the splinter in a level-2 forearm that has been
 * going bad for three days is more urgent than a clean level-4 that happens to
 * have a chip of shield in it.
 */
export function lodgedWound(wounds, part) {
    let worst = null;
    for (const wound of wounds) {
        if (!isLodged(wound))
            continue;
        if (part !== null && wound.part !== part)
            continue;
        if (!worst || wound.infectionMod > worst.infectionMod)
            worst = wound;
    }
    return worst;
}
/**
 * A part the axe is for: gangrenous, or destroyed and worth tidying.
 *
 * Only limbs. A gut wound that has gone septic has no surgical answer and the
 * design says so plainly (AoD): nursing, willow bark, and a TOU roll. The
 * refusal is spelled in prose in `amputateFault` rather than silently absent
 * from this list, because "there is nothing here to take off" is a lie when the
 * man is dying of his belly.
 */
export function amputablePart(catalogue, patient, part) {
    const kit = kitOf(catalogue, patient);
    if (!kit)
        return null;
    let best = null;
    for (const def of kit.plan.parts) {
        if (!isLimbZone(def.zone))
            continue;
        if (part !== null && def.id !== part)
            continue;
        const state = patient.vitals.body.parts[def.id];
        if (!state || state.severed)
            continue;
        const gangrenous = patient.vitals.wounds.some((wound) => wound.part === def.id && infectionStageOf(wound) === 'gangrenous');
        if (!gangrenous && !state.destroyed)
            continue;
        // A rotting limb outranks a ruined one: one is killing them and the other
        // is only in the way.
        if (!best || (gangrenous && !best.gangrenous)) {
            best = { part: def.id, zone: def.zone, gangrenous };
        }
    }
    return best;
}
/** Is anything on this body septic — rotten where a knife cannot follow? */
function septicSomewhere(patient) {
    return patient.vitals.wounds.some((wound) => infectionStageOf(wound) === 'septic');
}
/** What the surgeon has in their hand, by use. */
function wieldsUse(catalogue, surgeon, use) {
    return catalogue.weapon(surgeon.loadout.weaponId)?.uses?.includes(use) === true;
}
/* -------------------------------------------------------------------------- *
 * The gate
 * -------------------------------------------------------------------------- */
/**
 * Why this procedure may not happen, or `null`.
 *
 * Deliberately the same shape and the same scope rule as `medicalFault` —
 * yourself always, anybody else once they are down — rather than a call into
 * it: surgery asks two things medicine does not (a tool in the hand, a fire at
 * your back) and refuses for reasons medicine has no sentence for. What it does
 * *not* check is consent, which is `amputateHandler`'s business, because asking
 * is an action rather than a refusal.
 */
export function surgicalFault(world, catalogue, actor, targetId, tick, verb, part = null, asking = false) {
    const target = world.actors[targetId];
    if (!target)
        return 'there is nobody there';
    const patient = world.combat[targetId];
    if (!patient)
        return `${target.name} has no body to work on`;
    const surgeon = world.combat[actor.id];
    if (!surgeon || isHelpless(catalogue, surgeon, tick))
        return 'you are in no state to';
    if (targetId !== actor.id) {
        if (chebyshev(actor.x, actor.y, target.x, target.y) > 1) {
            return `${target.name} is out of reach`;
        }
        if (!isHelpless(catalogue, patient, tick) && !consentedTo(patient, actor.id, tick)) {
            // A conscious patient must be *lying still* for surgery, the same scope
            // rule the rest of the ladder uses — with one exception, and the
            // exception is the point: an awake person may agree to it, and consent is
            // how they say so.
            //
            // `asking` is that door, and it is open only to the intent's own check.
            // The handler needs to get as far as `apply` in order to *put the
            // question*, because asking somebody for their leg is an action rather
            // than a refusal; the channel's second look passes `false` and so cannot
            // finish a procedure on somebody who woke up and never agreed.
            if (!asking || verb !== 'amputate')
                return `${target.name} is still on their feet`;
        }
    }
    const capFault = capabilityFault(catalogue, surgeon, verb);
    if (capFault)
        return capFault;
    if (verb === 'extract') {
        if (!wieldsUse(catalogue, surgeon, FINE_USE))
            return 'you need a knife in your hand for that';
        return lodgedWound(patient.vitals.wounds, part)
            ? null
            : 'there is nothing left in the wound to dig for';
    }
    if (!wieldsUse(catalogue, surgeon, CLEAVE_USE))
        return 'you need an axe in your hand for that';
    // The stump is seared as the blow lands — an amputation without a fire is a
    // bleed nobody can close, and this refusal is the whole reason it happens in
    // a camp rather than where they fell.
    if (!fireAt(world, actor.x, actor.y, tick))
        return 'there is no fire to close the stump';
    if (!amputablePart(catalogue, patient, part)) {
        return septicSomewhere(patient)
            ? 'the rot is in the body, where no blade can follow'
            : 'there is nothing here that must come off';
    }
    return null;
}
/* -------------------------------------------------------------------------- *
 * Consent
 * -------------------------------------------------------------------------- */
/** Has this patient said yes to *this* surgeon, recently enough to count? */
export function consentedTo(patient, surgeonId, tick) {
    const consent = patient.consent;
    if (!consent || !consent.granted)
        return false;
    if (consent.surgeonId !== surgeonId)
        return false;
    return tick - consent.askedAt <= CONSENT_TICKS;
}
/** Put the question. The patient's client draws the modal off this event. */
function ask(ctx, actor, target, patient, part) {
    patient.consent = { surgeonId: actor.id, part, askedAt: ctx.tick, granted: false };
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['amputate', 'asks', part],
        text: `${actor.name} means to take ${target.name}'s ${part}. Allow it?`,
        // To the patient alone: it is their leg and their decision, and a prompt
        // broadcast to the clearing would be a prompt half the valley could answer.
        audience: 'actor',
        to: target.id,
        at: { x: target.x, y: target.y },
    });
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['amputate', 'asked', part],
        text: `${actor.name} asks ${target.name} for leave to take the ${part}.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/**
 * The patient's answer.
 *
 * Instant, like `execute` and for the mirror-image reason: a yes that took ten
 * seconds to give would be a yes given while somebody bled. A no clears the
 * request outright rather than marking it refused, so the surgeon may ask again
 * — arguing about a leg is the scene this milestone exists to produce, and a
 * one-shot veto would end it.
 */
export const consentHandler = {
    type: 'consent',
    parse: parseConsent,
    check(intent, ctx) {
        const patient = ctx.world.combat[intent.actorId];
        if (!patient)
            return 'you have no body to give leave with';
        const consent = patient.consent;
        if (!consent || consent.surgeonId !== intent.payload.surgeonId) {
            return 'nobody has asked you for that';
        }
        if (ctx.tick - consent.askedAt > CONSENT_TICKS)
            return 'that was asked too long ago';
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const patient = ctx.world.combat[intent.actorId];
        const surgeon = ctx.world.actors[intent.payload.surgeonId];
        if (!actor || !patient?.consent)
            return;
        const part = patient.consent.part;
        if (intent.payload.allow)
            patient.consent.granted = true;
        else
            patient.consent = null;
        ctx.emit({
            type: 'interacted',
            actors: [actor.id, intent.payload.surgeonId],
            refs: ['consent', intent.payload.allow ? 'given' : 'refused', part],
            text: intent.payload.allow
                ? `${actor.name} gives ${surgeon?.name ?? 'them'} leave to take the ${part}.`
                : `${actor.name} will not have the ${part} taken.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/* -------------------------------------------------------------------------- *
 * Extraction
 * -------------------------------------------------------------------------- */
/**
 * Dig the fragment out (M4-05).
 *
 * Success takes the standing infection modifier back off the wound — exactly
 * the `LODGED_INFECTION_MOD` that `createWound` added the day the tusk went in,
 * which is why that constant is a named export rather than a number in a table
 * — and drops the thing itself into the surgeon's satchel with its story
 * written on it. Failure is a fresh cut to try again through. A fumble drives it
 * deeper, and the wound gets worse.
 */
export function finishExtract(ctx, actor, target, patient, part) {
    const surgeon = ctx.world.combat[actor.id];
    const wound = lodgedWound(patient.vitals.wounds, part);
    if (!surgeon || !wound)
        return nothingToTreat(ctx, actor, target, 'extract');
    const kit = kitOf(ctx.catalogue, surgeon);
    const fineWork = kit ? deriveCapabilities(kit.plan, surgeon.vitals.body).fineWork : 0;
    const senseless = target.id !== actor.id && isHelpless(ctx.catalogue, patient, ctx.tick);
    const roll = rollTreatment(ctx.rng('medicine'), treatmentDice(surgeon, ctx.world.lore[actor.id], {
        self: target.id === actor.id,
        dark: workingBlind(ctx, actor),
        materialMod: 0,
        bonus: BLADE_BONUS + Math.round(fineWork * 2) + (senseless ? SENSELESS_BONUS : 0),
    }));
    if (roll.fumble) {
        wound.level = Math.min(MAX_WOUND_LEVEL, wound.level + 1);
        wound.pain += 1;
        ctx.emit({
            type: 'interacted',
            actors: [actor.id, target.id],
            refs: ['extract', 'deeper', wound.id, wound.part],
            text: `${actor.name} pushes it deeper, and ${patientPossessive(actor, target)} ${wound.zone} opens around the blade.`,
            audience: 'earshot',
            range: SURGERY_EARSHOT,
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    if (roll.successes === 0) {
        hurt(ctx, target, patient, wound.part, FAILED_DIG_LEVEL, `${actor.name}'s knife`);
        ctx.emit({
            type: 'interacted',
            actors: [actor.id, target.id],
            refs: ['extract', 'missed', wound.id, wound.part],
            text: `${actor.name} cuts and finds nothing but ${patientPossessive(actor, target)} ${wound.zone}.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    wound.extracted = true;
    wound.infectionMod = Math.max(0, wound.infectionMod - LODGED_INFECTION_MOD);
    wound.dressedAt = ctx.tick;
    learnMedicine(ctx, actor);
    // The keepsake. Non-stackable and noted, so two splinters out of two people
    // are two different things a player can be shown (M4-05's provenance).
    const inventory = (ctx.world.inventories[actor.id] ??= createInventory());
    addItem(inventory, FRAGMENT_ITEM, 1, ctx.tick, false, `cut from ${target.name}'s ${wound.zone}, ${wound.provenance.where}`);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['extract', 'done', wound.id, wound.part],
        text: `${actor.name} works it free of ${patientPossessive(actor, target)} ${wound.zone} and holds it up, wet.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/** What comes out. The one content id this module names — see `flora.ts`. */
export const FRAGMENT_ITEM = 'fragment';
/* -------------------------------------------------------------------------- *
 * Amputation
 * -------------------------------------------------------------------------- */
/**
 * Take the limb off (M4-05).
 *
 * No skill roll decides whether the blow lands — an axe swung at a held leg
 * with a fire beside you does what an axe does, and a game that let a novice
 * "miss" an amputation would be a game where the drama is dice rather than the
 * decision. What the dice decide is whether the rot was already past the cut.
 *
 * Everything after is consequence: the part is gone and every wound on it goes
 * with it, the stump is a fresh serious wound with its own clocks, and if they
 * were awake and undosed the pain very likely takes them out of the
 * conversation for a while.
 */
export function finishAmputate(ctx, actor, target, patient, part) {
    const surgeon = ctx.world.combat[actor.id];
    const choice = amputablePart(ctx.catalogue, patient, part);
    if (!surgeon || !choice)
        return nothingToTreat(ctx, actor, target, 'amputate');
    const state = patient.vitals.body.parts[choice.part];
    const kit = kitOf(ctx.catalogue, patient);
    if (!state || !kit)
        return nothingToTreat(ctx, actor, target, 'amputate');
    const awake = !isHelpless(ctx.catalogue, patient, ctx.tick);
    // The rot, and whether the cut was above it.
    const stopped = !choice.gangrenous || ctx.rng('medicine').next() < GANGRENE_STOPPED;
    // Everything that was wrong with the limb leaves with the limb. `permanent`
    // rather than `healed`, because this is not a wound that got better: it is
    // the record of what was taken, and the injury report has to keep saying so.
    for (const wound of patient.vitals.wounds) {
        if (wound.part !== choice.part)
            continue;
        wound.bleedRate = 0;
        wound.infectionClock = 0;
        wound.state = 'permanent';
        wound.healedAt = ctx.tick;
    }
    state.severed = true;
    const stump = hurt(ctx, target, patient, choice.part, STUMP_LEVEL, `${actor.name}'s axe`, 'surgery');
    // The 15%: the blade came down below the rot, and the stump is already going
    // the same way. Written onto the fresh wound, so the second conversation
    // starts from a wound somebody can examine.
    if (stump && !stopped) {
        stump.state = 'infected';
        stump.infectionClock = 1;
        stump.dirty = true;
    }
    // A conscious patient with nothing in them takes the whole of it. Relief —
    // willow, and the mead M5-01 brings — is subtracted through `feltPain`, the
    // same seam every other pain in the game passes through.
    if (awake && stump) {
        stump.pain += feltPain(patient.vitals, AWAKE_PAIN);
    }
    patient.consent = null;
    learnMedicine(ctx, actor);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: ['amputate', stopped ? 'done' : 'too-late', choice.part, awake ? 'awake' : 'senseless'],
        text: awake
            ? `${actor.name} takes ${target.name}'s ${choice.part} off. ${target.name} screams, and goes on screaming.`
            : `${actor.name} takes ${target.name}'s ${choice.part} off. ${target.name} does not wake.`,
        // The loudest thing in the valley. Everyone within a bowshot knows exactly
        // what is happening at that fire, which is the cost of the certain answer.
        audience: 'earshot',
        range: SURGERY_EARSHOT,
        at: { x: actor.x, y: actor.y },
    });
}
/* -------------------------------------------------------------------------- *
 * Wounds made on purpose
 * -------------------------------------------------------------------------- */
/**
 * Put a wound on somebody deliberately — the failed dig, and the stump.
 *
 * Straight through the table and `applyWound`, with `cause: 'surgery'` and a
 * provenance that names the hand that did it. That is the point: a stump has a
 * story ("Gwil's axe, by the fire at Llan-y-Nant") for exactly the same reason
 * a spear wound does, and in twenty sessions' time the injury report will still
 * say who took the leg.
 */
function hurt(ctx, target, patient, part, level, by, cause = 'surgery') {
    const kit = kitOf(ctx.catalogue, patient);
    const def = kit?.plan.parts.find((candidate) => candidate.id === part);
    if (!kit || !def)
        return null;
    const entry = ctx.catalogue.woundTable?.resolve('cut', def.zone, level);
    if (!entry)
        return null;
    const wound = createWound({
        id: `surgery:${ctx.tick}:${target.id}:${part}`,
        part,
        zone: def.zone,
        type: 'cut',
        level,
        entry,
        inflictedAt: ctx.tick,
        provenance: {
            cause,
            weapon: by,
            where: `at (${target.x}, ${target.y})`,
            narrative: `${by} opened ${target.name}'s ${def.name}.`,
        },
    });
    applyWound(patient.vitals.body, patient.vitals.wounds, wound);
    return wound;
}
/* -------------------------------------------------------------------------- *
 * Handlers
 * -------------------------------------------------------------------------- */
function surgicalHandler(verb, parse, begin) {
    return {
        type: verb,
        parse,
        check(intent, ctx) {
            const actor = ctx.world.actors[intent.actorId];
            if (!actor)
                return `${intent.actorId} is not in the world`;
            if (!ctx.world.combat[intent.actorId])
                return `${intent.actorId} has no hands to do it with`;
            const targetId = intent.payload.targetId ?? intent.actorId;
            return surgicalFault(ctx.world, ctx.catalogue, actor, targetId, ctx.tick, verb, intent.payload.part ?? null, true);
        },
        apply(intent, ctx) {
            const actor = ctx.world.actors[intent.actorId];
            const surgeon = ctx.world.combat[intent.actorId];
            if (!actor || !surgeon)
                return;
            begin(ctx, actor, surgeon, intent.payload.targetId ?? intent.actorId, intent.payload.part ?? null);
        },
    };
}
export const extractHandler = surgicalHandler('extract', parseExtract, (ctx, actor, surgeon, targetId, part) => {
    beginSurgery(ctx, actor, surgeon, 'extract', targetId, part);
});
/**
 * Amputation, which asks before it cuts.
 *
 * The one verb in the game whose intent may resolve into a *question* rather
 * than an action: against a patient who can answer and has not, this puts the
 * request on their body and stops. The player has spent their action asking,
 * which is right — asking a man for his leg is a thing you do, not a menu.
 */
export const amputateHandler = surgicalHandler('amputate', parseAmputate, (ctx, actor, surgeon, targetId, part) => {
    const target = ctx.world.actors[targetId];
    const patient = ctx.world.combat[targetId];
    if (!target || !patient)
        return;
    const choice = amputablePart(ctx.catalogue, patient, part);
    if (!choice)
        return;
    if (targetId !== actor.id &&
        !isHelpless(ctx.catalogue, patient, ctx.tick) &&
        !consentedTo(patient, actor.id, ctx.tick)) {
        ask(ctx, actor, target, patient, choice.part);
        return;
    }
    beginSurgery(ctx, actor, surgeon, 'amputate', targetId, choice.part);
});
/**
 * Open a surgical channel.
 *
 * A copy of `beginChannel`'s shape rather than a call into `interact.ts`,
 * because the dependency runs the other way — the channel loop imports these
 * finishers — and because the opening *line* is the thing worth writing
 * separately: a procedure announces itself to the clearing in a sentence
 * nobody misreads.
 */
function beginSurgery(ctx, actor, surgeon, verb, targetId, part) {
    const channel = {
        verb,
        targetId,
        startedAt: ctx.tick,
        completesAt: ctx.tick + (verb === 'extract' ? EXTRACT_TICKS : AMPUTATE_TICKS),
        fromX: actor.x,
        fromY: actor.y,
        detail: part,
    };
    surgeon.channel = channel;
    const target = ctx.world.actors[targetId];
    const self = targetId === actor.id;
    const whose = self ? 'their own' : `${target?.name ?? 'someone'}'s`;
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, targetId],
        refs: [verb, 'begun', part ?? ''],
        text: verb === 'extract'
            ? `${actor.name} sets a knife to ${whose} wound and begins to dig.`
            : `${actor.name} lays ${whose} limb straight and takes up the axe.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
export const SURGERY_INTENTS = [
    extractHandler,
    amputateHandler,
    consentHandler,
];
/**
 * Forget a question nobody answered.
 *
 * A system rather than a check at the point of use, because a stale request is
 * visible: the patient's client is drawing a modal off it. Left to expire
 * lazily, a player who walked away from the question would still be looking at
 * it an hour later.
 */
export const consentSystem = {
    name: 'consent',
    phase: 'sim',
    run(ctx) {
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const combatant = ctx.world.combat[id];
            const consent = combatant?.consent;
            if (!combatant || !consent)
                continue;
            if (ctx.tick - consent.askedAt <= CONSENT_TICKS)
                continue;
            combatant.consent = null;
            const actor = ctx.world.actors[id];
            if (!actor)
                continue;
            ctx.emit({
                type: 'interacted',
                actors: [id, consent.surgeonId],
                refs: ['consent', 'lapsed', consent.part],
                text: `${actor.name} has not answered.`,
                audience: 'actor',
                to: consent.surgeonId,
                at: { x: actor.x, y: actor.y },
            });
        }
    },
};
//# sourceMappingURL=surgery.js.map